import React from 'react'
import classNames from 'classnames'

import LeftArrowIcon from 'components/LeftArrowIcon'
import CircleInfoIcon from 'components/CircleInfoIcon'
import CenteredContent from 'components/CenteredContent'

import { Grid, Typography, IconButton, Tabs, Tab, AppBar } from '@material-ui/core'
import { createStyles, WithStyles, withStyles, Theme, WithTheme } from '@material-ui/core/styles'
import { AppBarProps } from '@material-ui/core/AppBar'

import { Omit } from 'types'

type Tab = {
  title: React.ReactNode
  component?: React.ComponentType<{}>
}

interface Props extends Omit<AppBarProps, 'title'> {
  dark?: boolean
  title: React.ReactNode
  onBackClick?: () => void
  onInfoClick?: () => void
  tabs?: Array<Tab>
  selectedTab?: number
  onTabSelect?: (tabIndex: number) => void
}

const headerHeight = 60
const tabHeight = 48

const styles = (theme: Theme) => createStyles({
  root: {
    backgroundColor: theme.palette.background.default,
    display: 'block',
  },
  tabs: {
    height: tabHeight,
  },
  header: {
    height: headerHeight,
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
  },
  missingIcon: {
    width: 48,
    height: 48,
  },
  tab: {
    minWidth: 'auto',
    flex: 1,
  },
})

type InjectedProps = Props & WithStyles<typeof styles> & WithTheme

const NavigationHeader: React.SFC<InjectedProps> = ({
  dark,
  title,
  onBackClick,
  onInfoClick,
  classes,
  className,
  tabs,
  selectedTab,
  onTabSelect,
  theme,
  ...props
}) => {
  const color = dark ? theme.palette.secondary.light : theme.palette.text.secondary
  let rightChild = <div className={classes.missingIcon} />

  if (onInfoClick) {
    rightChild = (
      <IconButton onClick={onInfoClick}>
        <CircleInfoIcon style={{ fill: color }} />
      </IconButton>
    )
  }

  const shouldRenderTabs = Boolean(tabs && tabs.length > 0 && onTabSelect)

  return (
    <AppBar position="sticky" elevation={0} className={classNames(classes.root, className)} {...props}>
      <Grid container alignItems="center" className={classes.header}>
        <Grid item>
          <IconButton onClick={onBackClick}>
            <LeftArrowIcon style={{ fill: color }} />
          </IconButton>
        </Grid>
        <Grid item xs>
          <Typography variant="body1" align="center" style={{ color }}>{title}</Typography>
        </Grid>
        <Grid item>
          {rightChild}
        </Grid>
      </Grid>
      {shouldRenderTabs && (
        <CenteredContent>
          <Tabs
            variant="fullWidth"
            className={classes.tabs}
            value={selectedTab}
            textColor="inherit"
            indicatorColor="primary"
          >
            {tabs!.map((tab, i) => (
              <Tab
                key={i}
                label={tab.title}
                className={classes.tab}
                onClick={() => onTabSelect!(i)}
              />
            ))}
          </Tabs>
        </CenteredContent>
      )}
    </AppBar>
  )
}

export default withStyles(styles, { withTheme: true })(NavigationHeader)
